import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, Popover, Chip, Radio, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AppsIcon from '@mui/icons-material/Apps';
import GradeIcon from '@mui/icons-material/Grade';
import LanguageIcon from '@mui/icons-material/Language';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CategoryIcon from '@mui/icons-material/Category';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import './FilterPapers.css';
import { MainContext } from '../../../../../../context/main/main-context';
import SimpleReactValidator from "simple-react-validator";
import Type from '../../../../subscription/Type';
import ExamsHeaderTabs from '../../ExamsHeaderTabs';

// Filter icons mapping
const filterIconMap = {
  Subject: <MenuBookIcon fontSize="small" />,
  Grade: <GradeIcon fontSize="small" />,
  Language: <LanguageIcon fontSize="small" />,
  Exam_Category: <AppsIcon fontSize="small" />,
  Category: <CategoryIcon fontSize="small" />,
  Status: <AssignmentIcon fontSize="small" />,
  Year: <CalendarTodayIcon fontSize="small" />,
  Created_By: <PersonIcon fontSize="small" />,
  Exam_Status: <AssignmentIcon fontSize="small" />,
};

// Chip color mapping for different filter types
const chipColorMap = {
  Exam_Category: { bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' },
  Grade: { bg: '#FCE4EC', color: '#C2185B', border: '#F48FB1' },
  Language: { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  Subject: { bg: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' },
  Category: { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  Status: { bg: '#FFF8E1', color: '#F57F17', border: '#FFE082' },
  Year: { bg: '#E0F7FA', color: '#00838F', border: '#80DEEA' },
  Created_By: { bg: '#EDE7F6', color: '#512DA8', border: '#B39DDB' },
  Exam_Status: { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
  default: { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' },
};


const filterKeyMap = {
  Subject: { idKey: "subject_id", labelKey: "subject" },
  Grade: { idKey: "grade_id", labelKey: "grade" },
  Status: { idKey: "status_id", labelKey: "status" },
  Source: { idKey: "source_id", labelKey: "source" },
  Author: { idKey: "author_id", labelKey: "author" },
  Category: { idKey: "category_id", labelKey: "name" },
  Exam_Category: { idKey: "exam_category_id", labelKey: "name" },
  Chapter: { idKey: "chapter_id", labelKey: "chapter" },
  Difficulty: { idKey: "difficulty_id", labelKey: "difficulty_type" },
  Language: { idKey: "language_id", labelKey: "language" },
  Created_By: { idKey: "user_id", labelKey: (item) => `${item.first_name} ${item.last_name}` },
  Reviewers: { idKey: "user_id", labelKey: (item) => `${item.first_name} ${item.last_name}` },
  Term: { idKey: "term_id", labelKey: "term" },
  Exam_Status: { idKey: "exam_status_id", labelKey: "exam_status" },
  Year: { idKey: "year_id", labelKey: "year" },
  Type: { idKey: "type_id", labelKey: "qtype" }
};


export default function FilterPanel({
  userType, selectedFilters, setSelectedFilters,
  filterOptions, setFilterOptions, visibleFilterOptions, isShowExamStatus
}) {



  const [anchorEl, setAnchorEl] = useState(null);
  const [currentDropdown, setCurrentDropdown] = useState('');
  const [showAllChips, setShowAllChips] = useState({});
  const [, forceUpdate] = useState(); // Used to force re-renders for the validator

  const handleOpen = (event, filterType) => {
    setAnchorEl(event.currentTarget);
    setCurrentDropdown(filterType);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentDropdown('');
  };

  const handleToggle = (filterType, value) => {

    if (filterType === "Exam_Status") {
      setSelectedFilters({
        ...selectedFilters,
        [filterType]: [value]
      });
    } else {
      const current = selectedFilters[filterType] || [];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];

      setSelectedFilters({
        ...selectedFilters,
        [filterType]: updated
      });
    }
  };


  const handleDeleteChip = (filterType, value) => {
    const updatedFilters = {
      ...selectedFilters,
      [filterType]: selectedFilters[filterType].filter((item) => item !== value)
    };

    setSelectedFilters(updatedFilters);
  };

  const handleClearAll = () => {
    const cleared = {};
    Object.keys(selectedFilters).forEach((key) => {
      if (isShowExamStatus && key === "Exam_Status") {
        cleared[key] = selectedFilters[key];
      } else {
        cleared[key] = [];
      }
    });
    setSelectedFilters(cleared);
  };

  const toggleShowAllChips = (filterType) => {
    setShowAllChips(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  };

  const open = Boolean(anchorEl);

  const [validator] = useState(
    new SimpleReactValidator({
      messages: {
        className: 'srv-validation-message',
        required: "This field is required.",
        before: "Start date must be before end date.",
        after: "End date must be after start date.",
      },
      validators: {
        before: {
          message: 'Start date must be before end date.',
          rule: (val, params, validator) => {
            if (!val || !params[0]) return true; // skip if any empty
            const startDate = new Date(val);
            const endDate = new Date(params[0]);
            return startDate <= endDate;
          }
        },
        after: {
          message: 'End date must be after start date.',
          rule: (val, params, validator) => {
            if (!val || !params[0]) return true; // skip if any empty
            const startDate = new Date(params[0]);
            const endDate = new Date(val);
            return endDate >= startDate;
          }
        }
      }
    })
  );

  const handleStartDateChange = (event) => {
    const date = event.target.value;
    const formattedDate = date ? date.replace(/-/g, "") : null;
    const newFilters = {
      ...selectedFilters,
      StartDate: formattedDate,
      StartDateRaw: date
    };
    setSelectedFilters(newFilters);
    validator.showMessageFor("StartDate");
  };

  const handleEndDateChange = (event) => {
    const date = event.target.value;
    const formattedDate = date ? date.replace(/-/g, "") : null;
    const newFilters = {
      ...selectedFilters,
      EndDate: formattedDate,
      EndDateRaw: date
    };
    setSelectedFilters(newFilters);
    validator.showMessageFor("EndDate");
  };


  return (
    <div className="ph-filter-panel-modern">
      {/* Filter Dropdowns Row */}
      <div className="ph-filter-dropdowns-row">
        {userType && visibleFilterOptions
          ?.filter(filterType => filterType !== 'StartDate' && filterType !== 'EndDate' && (isShowExamStatus || filterType !== "Exam_Status"))
          .map((filterType) => {
            const isActive = selectedFilters[filterType]?.length > 0;
            const displayName = filterType.replace(/_/g, ' ');

            return (
              <button
                key={filterType}
                className={`ph-filter-dropdown-btn ${isActive ? 'active' : ''} ${currentDropdown === filterType ? 'open' : ''}`}
                onClick={(e) => handleOpen(e, filterType)}
              >
                <span className="ph-filter-icon">{filterIconMap[filterType] || <AppsIcon fontSize="small" />}</span>
                <span className="ph-filter-label">{displayName}</span>
                <KeyboardArrowDownIcon className="ph-filter-arrow" />
              </button>
            );
          })}

        {/* Clear Filters Button */}
        <button className="ph-clear-filters-btn" onClick={handleClearAll}>
          <DeleteForeverIcon fontSize="small" />
          <span>Clear Filters</span>
        </button>
      </div>

      {/* Date Range Filters */}
      {(visibleFilterOptions.includes("StartDate") || visibleFilterOptions.includes("EndDate")) && (
        <div className="ph-date-filters-row">
          <label className="ph-date-label">
            <CalendarTodayIcon fontSize="small" />
            <span>Start Date:</span>
            <input
              type="date"
              className="ph-date-input"
              value={selectedFilters.StartDateRaw || ""}
              onChange={handleStartDateChange}
            />
            {validator.message("StartDate", selectedFilters.StartDateRaw, `before:${selectedFilters.EndDateRaw}`)}
          </label>

          <label className="ph-date-label">
            <CalendarTodayIcon fontSize="small" />
            <span>End Date:</span>
            <input
              type="date"
              className="ph-date-input"
              value={selectedFilters.EndDateRaw || ""}
              onChange={handleEndDateChange}
            />
            {validator.message("EndDate", selectedFilters.EndDateRaw, `after:${selectedFilters.StartDateRaw}`)}
          </label>
        </div>
      )}

      {/* Selected Filters Chips Row */}
      <Stack direction="row" spacing={0.5} className="ph-selected-chips-row" sx={{ flexWrap: 'wrap' }}>
        {userType && visibleFilterOptions
          ?.filter(filterType => filterType !== 'StartDate' && filterType !== 'EndDate' && (isShowExamStatus || filterType !== "Exam_Status"))
          .map((filterType) => {
            const chipColors = chipColorMap[filterType] || chipColorMap.default;

            return selectedFilters[filterType]?.map((value) => {
              const config = filterKeyMap[filterType];
              if (!config) return null;

              const item = filterOptions[filterType]?.find(opt => opt[config.idKey] === value);
              let labelName = typeof config.labelKey === "function" ?
                config.labelKey(item) : item?.[config.labelKey];

              return (
                <Chip
                  key={`${filterType}-${value}`}
                  label={labelName || value}
                  onDelete={filterType !== "Exam_Status" ? () => handleDeleteChip(filterType, value) : undefined}
                  size="small"
                  className="ph-selected-chip"
                  sx={{
                    backgroundColor: chipColors.bg,
                    color: chipColors.color,
                    border: `1px solid ${chipColors.border}`,
                    fontWeight: 600,
                    height: 'auto',
                    fontSize: 10,
                    padding: '1px 4px',
                    boxShadow: 'none',
                    '& .MuiChip-deleteIcon': {
                      color: chipColors.color,
                      fontSize: 10
                    }
                  }}
                />
              );
            });
          })}
      </Stack>

      {/* Popover for filter options */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: 'ph-dropdown-popover' }}
      >
        <div className="ph-dropdown-header d-flex justify-content-between align-items-center">
          {currentDropdown !== "Exam_Status" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    filterOptions[currentDropdown]?.length > 0 &&
                    filterOptions[currentDropdown]?.every(opt => {
                      const config = filterKeyMap[currentDropdown];
                      const value = opt[config.idKey];
                      return selectedFilters[currentDropdown]?.includes(value);
                    })
                  }
                  indeterminate={
                    selectedFilters[currentDropdown]?.length > 0 &&
                    selectedFilters[currentDropdown]?.length < filterOptions[currentDropdown]?.length
                  }
                  onChange={(e) => {
                    const config = filterKeyMap[currentDropdown];
                    if (e.target.checked) {
                      const allValues = filterOptions[currentDropdown]?.map(opt => opt[config.idKey]) || [];
                      setSelectedFilters({
                        ...selectedFilters,
                        [currentDropdown]: allValues
                      });
                    } else {
                      setSelectedFilters({
                        ...selectedFilters,
                        [currentDropdown]: []
                      });
                    }
                  }}
                  size="small"
                  sx={{
                    color: '#7C3AED',
                    '&.Mui-checked': { color: '#7C3AED' },
                  }}
                />
              }
              label="Select All"
            />
          )}
          <CloseIcon fontSize="small" onClick={handleClose} style={{ cursor: 'pointer' }} />
        </div>

        <div className="ph-dropdown-body">
          {(filterOptions[currentDropdown] || []).map((option, idx) => {
            const value =
              option?.user_id ||
              option?.grade_id ||
              option?.language_id ||
              option?.subject_id ||
              option?.term_id ||
              option?.year_id ||
              option?.source_id ||
              option?.status_id ||
              option?.author_id ||
              option?.chapter_id ||
              option?.category_id ||
              option?.exam_category_id ||
              option?.difficulty_id ||
              option?.type_id ||
              option?.exam_status_id;

            const label =
              option?.name ||
              option?.grade ||
              option?.language ||
              option?.subject ||
              option?.term ||
              option?.year ||
              option?.source ||
              option?.status ||
              option?.author ||
              option?.chapter ||
              option?.difficulty_type ||
              option?.exam_status ||
              option?.qtype ||
              `${option.first_name} ${option.last_name}` ||
              "Unknown";

            return (
              <FormControlLabel
                key={`${value}-${idx}`}
                control={
                  currentDropdown === "Exam_Status" ?
                    <Radio
                      checked={selectedFilters[currentDropdown]?.includes(value)}
                      onChange={() => handleToggle(currentDropdown, value)}
                      value={value}
                      sx={{
                        color: '#7C3AED',
                        '&.Mui-checked': {
                          color: '#7C3AED',
                        },
                      }}
                    />
                    :
                    <Checkbox
                      checked={selectedFilters[currentDropdown]?.includes(value)}
                      onChange={() => handleToggle(currentDropdown, value)}
                      size="small"
                      sx={{
                        color: '#7C3AED',
                        '&.Mui-checked': {
                          color: '#7C3AED',
                        },
                      }}
                    />
                }
                label={label}
              />
            );
          })}
        </div>
      </Popover>
    </div>
  );
}
